'use client';

import { useState } from 'react';

import { useSetAtom, useStore } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'use-intl';

import {
  getMobileUtUniqueId,
  playOrEnqueueAudioAtom,
  useProjectResponseModeContext,
} from '@/shared-survey';
import { stopAudioPlayback } from '@/shared-ui';
import { isSurveyExpiredError, playAckSound } from '@/shared-utilities';

import { resolveConversationOverAction } from './project-response.helpers';
import type { HandsFreeContentPopup } from './project-usability-handsfree-content-popup.hooks';
import type { HandsFreeContentSession } from './project-usability-handsfree-content-session.hooks';
import type { HandsFreeContentVoice } from './project-usability-handsfree-content-voice.hooks';
import {
  useHandsFreePlayPause,
  useHandsFreeRecordingLifecycle,
} from './project-usability-handsfree-controls.hooks';
import { useMobileUtIntegration } from './project-usability-handsfree-mobile-ut.hooks';
import { useHandsFreeWelcomeDismiss } from './project-usability-handsfree-orchestration.hooks';
import { useHandsFreeSessionOver } from './project-usability-handsfree-session-over.hooks';
import {
  handsFreeHasEndedAtom,
  handsFreeHasStartedAtom,
} from './project-usability-handsfree.atoms';

interface HandsFreeContentLifecycleOptions
  extends
    Pick<
      HandsFreeContentSession,
      | 'isRecording'
      | 'isCurrentlyPaused'
      | 'isAiVoiceEnabled'
      | 'isConversationOver'
      | 'isFinalizingRecording'
      | 'insightId'
      | 'updateActivity'
      | 'pauseRecording'
      | 'resumeRecording'
      | 'setIsUserPaused'
      | 'resetAudioRecord'
      | 'startRecording'
      | 'stopRecording'
      | 'endRecording'
      | 'setIsHelpExpanded'
      | 'handleSubmitWithSidebar'
      | 'handleConversationOver'
      | 'getCompletionRedirectUrl'
    >,
    Pick<
      HandsFreeContentVoice,
      'isSpeaking' | 'hasPendingAudio' | 'hasUrlOrTask' | 'forceStopAiPlayback'
    >,
    Pick<
      HandsFreeContentPopup,
      'hidePopup' | 'lastAssistantMessage' | 'replyFeedbackMessage'
    > {
  isMobileMode: boolean;
}

export interface HandsFreeContentLifecycle extends Pick<
  ReturnType<typeof useHandsFreePlayPause>,
  'showPlayIcon' | 'handleResumeUserPause'
> {
  isSessionOver: boolean;
  showWelcomePrompt: boolean;
  hasRequestedStart: boolean;
  handleStartRecording: () => void;
  handlePlayPauseClick: () => void;
  playPauseAriaLabel: string;
  handlePermissionRetry: () => void;
  handlePopupSubmit: (content: string) => Promise<void>;
  handleConversationOverWithStop: ReturnType<
    typeof resolveConversationOverAction
  >;
}

export function useHandsFreeContentLifecycle({
  isMobileMode,
  isRecording,
  isCurrentlyPaused,
  isAiVoiceEnabled,
  isConversationOver,
  isFinalizingRecording,
  insightId,
  updateActivity,
  pauseRecording,
  resumeRecording,
  setIsUserPaused,
  resetAudioRecord,
  startRecording,
  stopRecording,
  endRecording,
  setIsHelpExpanded,
  handleSubmitWithSidebar,
  handleConversationOver,
  getCompletionRedirectUrl,
  isSpeaking,
  hasPendingAudio,
  hasUrlOrTask,
  forceStopAiPlayback,
  hidePopup,
  lastAssistantMessage,
  replyFeedbackMessage,
}: HandsFreeContentLifecycleOptions): HandsFreeContentLifecycle {
  const t = useTranslations('Usability');
  const searchParams = useSearchParams();
  const mobileUniqueId = getMobileUtUniqueId(searchParams);

  const [hasRequestedStart, setHasRequestedStart] = useState(false);

  const {
    responseMode,
    ingestTranscript,
    eventsObservable,
    startListening,
    stopListening,
  } = useProjectResponseModeContext();

  const store = useStore();
  const playOrEnqueueAudio = useSetAtom(playOrEnqueueAudioAtom);

  useHandsFreeRecordingLifecycle({ stopRecording });

  const { showPlayIcon, handlePauseRecording, handleResumeUserPause } =
    useHandsFreePlayPause({
      isRecording,
      isCurrentlyPaused,
      isAiVoiceEnabled,
      responseMode,
      isConversationOver,
      updateActivity,
      pauseRecording,
      resumeRecording,
      stopListening,
      startListening,
      setIsUserPaused,
      resetAudioRecord,
      stopAudioPlayback,
      hidePopup,
      playOrEnqueueAudio,
      lastAssistantMessage,
      replyFeedbackMessage,
    });

  const { isSessionOver } = useHandsFreeSessionOver({
    isConversationOver,
    isSpeaking,
    hasPendingAudio,
    isRecording,
    isFinalizingRecording,
    isAiVoiceEnabled,
    endRecording: () => {
      endRecording();
      store.set(handsFreeHasStartedAtom, false);
    },
    markEnded: () => {
      store.set(handsFreeHasEndedAtom, true);
    },
    getHasEnded: () => store.get(handsFreeHasEndedAtom),
  });

  useMobileUtIntegration({
    isMobileMode,
    mobileUniqueId,
    insightId,
    isRecording,
    isSessionOver,
    isFinalizingRecording,
    isVoiceCommandBlocked:
      isCurrentlyPaused || isConversationOver || isSessionOver,
    ingestTranscript,
    eventsObservable,
    startListening,
    getCompletionRedirectUrl,
  });

  const { showWelcomePrompt, setShowWelcomePrompt } =
    useHandsFreeWelcomeDismiss(() => setIsHelpExpanded(true));

  const handleStartRecording = () => {
    if (!store.get(handsFreeHasStartedAtom)) {
      store.set(handsFreeHasStartedAtom, true);
      store.set(handsFreeHasEndedAtom, false);
      setHasRequestedStart(true);
      updateActivity();
      if (hasUrlOrTask) {
        void startRecording();
      } else {
        setShowWelcomePrompt(false);
      }
    }
  };

  const handlePlayPauseClick = () => {
    if (!isRecording) handleStartRecording();
    else if (isCurrentlyPaused) handleResumeUserPause();
    else handlePauseRecording();
  };

  const getPlayPauseAriaLabel = () => {
    if (!isRecording) return t('startRecordingAria');
    if (isCurrentlyPaused) return t('resumeRecordingAria');
    return t('pauseRecordingAria');
  };

  const handlePermissionRetry = () => {
    store.set(handsFreeHasStartedAtom, false);
    store.set(handsFreeHasEndedAtom, false);
    window.location.reload();
  };

  const handlePopupSubmit = async (content: string) => {
    forceStopAiPlayback();
    const submitted = handleSubmitWithSidebar({ content });
    playAckSound();
    try {
      await submitted;
    } catch (error) {
      if (!isSurveyExpiredError(error)) throw error;
    }
  };

  const handleConversationOverWithStop = resolveConversationOverAction({
    onConversationOver: handleConversationOver,
    beforeConversationOver: forceStopAiPlayback,
    isMobileRelay: isMobileMode,
  });

  return {
    showPlayIcon,
    handleResumeUserPause,
    isSessionOver,
    showWelcomePrompt,
    hasRequestedStart,
    handleStartRecording,
    handlePlayPauseClick,
    playPauseAriaLabel: getPlayPauseAriaLabel(),
    handlePermissionRetry,
    handlePopupSubmit,
    handleConversationOverWithStop,
  };
}
